import { FC, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { concatMap, filter, Observable, of, take, throwError, timer } from 'rxjs';
import { useLoggedinUser } from '@hooks';
import { ElectrificationStatus, electrificationWizardService, notificationService } from '@services';
import { Player } from '@lottiefiles/react-lottie-player';
import { Stack } from '@mui/material';
// import wizard from '@assets/lottie/wizard.json';
// import magician from '@assets/lottie/magician.json';
import electricityLoading from '@assets/lottie/electricity-loading.json';

const POLLING_INTERVAL = 5000;
const MAX_WAITING_DURATION = 30000;

export const WizardProcessing: FC = () => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const navigate = useNavigate();
	const lottieRef = useRef<Player>(null);
	const userSellVehicles = user?.isDealer(user?.role);
	const userSellInfra = user?.isChargingPartner(user?.role);

	const getObservable = (): Observable<unknown> => {
		if (userSellVehicles) {
			return timer(POLLING_INTERVAL).pipe(
				concatMap(() => electrificationWizardService.getLatest()),
				concatMap((result, i) => (i < Math.floor(MAX_WAITING_DURATION / POLLING_INTERVAL) ? of(result) : throwError(() => result))),
				filter((result) => result && [ElectrificationStatus.COMPLETED, ElectrificationStatus.ERROR].includes(result.status)),
				concatMap((result) => (result.status === ElectrificationStatus.ERROR ? throwError(() => result) : of(result)))
			);
		} else if (userSellInfra) {
			return timer(POLLING_INTERVAL).pipe(take(1));
		} else {
			return timer(POLLING_INTERVAL).pipe(
				take(1),
				concatMap(() => throwError(() => new Error('unknown seller type')))
			);
		}
	};

	useEffect(() => {
		const subscription = getObservable()?.subscribe({
			next: () => navigate('../result'),
			error: () => {
				notificationService.sendError(t('wizard.create-error'));
				navigate('../start');
			}
		});

		return () => subscription?.unsubscribe();
	}, [user]);

	return (
		<Stack direction="column" alignItems="center" justifyContent="center" sx={{ height: 1.0 }}>
			<Player ref={lottieRef} autoplay loop src={electricityLoading} speed={0.75} style={{ height: 300, width: 300 }} />
		</Stack>
	);
};
